import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  CircularProgress,
} from '@mui/material';
import { AuthContext } from '../context/AuthContext';
import { login as loginApi } from '../services/api';
import Logo from './Logo';

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const userData = await loginApi(username, password);

      if (userData.role === 'client') {
        setError('Client login is not allowed here. Please use the Client Portal.');
        return;
      }

      login(userData);
      navigate(`/${userData.role}`);
    } catch (err) {
      console.error('Login error:', err);
      if (err.response) {
        setError(err.response.data?.detail || 'Login failed. Please check your credentials.');
      } else if (err.request) {
        setError('Network error: Could not reach the server. Please check your connection.');
      } else if (err.message) {
        setError(`Error: ${err.message}`);
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 4, sm: 6 },
        px: { xs: 2, sm: 3 },
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="sm" sx={{ px: { xs: 1, sm: 2 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            <Logo size="large" color="primary" />
          </Box>
          <Typography
            component="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.875rem', sm: '2.25rem' },
              letterSpacing: '-0.035em',
              color: 'text.primary',
              lineHeight: 1.1,
            }}
          >
            Aromahpure Air
          </Typography>
          <Typography
            sx={{
              mt: 1,
              color: 'text.secondary',
              fontSize: { xs: '0.9375rem', sm: '1rem' },
              letterSpacing: '-0.01em',
              maxWidth: 320,
              mx: 'auto',
            }}
          >
            Sign in to manage machines, clients, and field work.
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            width: '100%',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: 'none',
          }}
        >
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              mb: 2.5,
              color: 'text.secondary',
              letterSpacing: '0.06em',
              fontWeight: 600,
              fontSize: '0.6875rem',
            }}
          >
            Staff access
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2.5 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              label="Username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              margin="normal"
              required
              helperText="Clients should use the Client Portal"
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={submitting}
              sx={{ mt: 3, mb: 1 }}
            >
              {submitting ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                'Sign In'
              )}
            </Button>
          </Box>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
              Are you a client?
            </Typography>
            <Link
              href="/client-login"
              underline="hover"
              sx={{
                fontSize: '0.9375rem',
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: '-0.01em',
              }}
            >
              Go to Client Portal
            </Link>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default Login;
